/**
 * Document revision info for Working Document details dialog.
 * State: documentRevisionData, loading, message. Fetch when dialog opens.
 */
import { useCallback, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../hooks";
import {
  fetchDocumentRevisionInfo,
  clearDocumentRevisionInfo,
} from "../slices/documents/documentRevisionInfoSlice";

export function useDocumentRevisionInfo(selectedDocument, isOpen, skipFetch = false) {
  const dispatch = useAppDispatch();
  const state = useAppSelector(
    (rootState) => rootState.DocumentsSlice?.documentRevisionInfo
  );

  const documentRevisionData = state?.documentRevisionData ?? {};
  const isViewCopyExists = state?.isViewCopyExists ?? null;
  const loading = state?.loading ?? false;
  const message = state?.message ?? null;

  const fetchRevisionInfo = useCallback(
    (file) => {
      if (!file?.id) return;
      dispatch(fetchDocumentRevisionInfo(file));
    },
    [dispatch]
  );

  const clearRevisionInfo = useCallback(() => {
    dispatch(clearDocumentRevisionInfo());
  }, [dispatch]);

  // When dialog opens with a document, fetch revision info; when it closes, clear.
  useEffect(() => {
    if (!isOpen) {
      clearRevisionInfo();
      return;
    }
    if (skipFetch) return;
    if (!selectedDocument?.id) return;
    fetchRevisionInfo(selectedDocument);
  }, [isOpen, selectedDocument?.id, skipFetch]);

  const isComplete = !loading && !message && Object.keys(documentRevisionData).length > 0;
  // Only true empty IFS response (204); do not treat API/load errors as "no details" — those use isError.
  const isNoData = !loading && message === "No document details found.";
  const isError = !loading && !!message && !isNoData;

  return {
    documentRevisionData,
    isViewCopyExists,
    loading,
    message,
    isComplete,
    isNoData,
    isError,
    fetchRevisionInfo,
    clearRevisionInfo,
  };
}
